import React, { useState, useEffect, useRef } from 'react';
import { PlayCircle, PauseCircle, Plus, Minus } from 'lucide-react';
import '../styles/BatchAudioPlayer.css'; 

const BatchAudioPlayer = ({ files, currentFolderId, user, onUpdateProgress, progressData, onAudioChange, onPlaybackComplete, isManualPlay, currentPlayingFile, audioGap, repeats }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [repeatCount, setRepeatCount] = useState(1);
  const [currentAudioIndex, setCurrentAudioIndex] = useState(0);
  const [currentRepetition, setCurrentRepetition] = useState(1);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [audioDuration, setAudioDuration] = useState(null);
  const [currentText, setCurrentText] = useState('');
  const [isTextVisible, setIsTextVisible] = useState(true);
  const [previousText, setPreviousText] = useState('');

  const audioRef = useRef(null);
  const playTimeRef = useRef(0);
  const playStartTimeRef = useRef(null);

  const audioFiles = files.filter(file => file.mimeType.startsWith('audio/'));

  useEffect(() => {
    setIsPlaying(false);
    setCurrentAudioIndex(0);
    setCurrentRepetition(1);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  }, [currentFolderId]);

  useEffect(() => {
    if (audioRef.current) {
      const handleLoadedMetadata = () => {
        setAudioDuration(audioRef.current.duration);
      };

      audioRef.current.addEventListener('loadedmetadata', handleLoadedMetadata);
      return () => {
        audioRef.current?.removeEventListener('loadedmetadata', handleLoadedMetadata);
      };
    }
  }, [audioRef.current]);

  useEffect(() => {
    if (currentAudioIndex >= 0 && currentAudioIndex < audioFiles.length) {
      onAudioChange?.(audioFiles[currentAudioIndex]);
    }
  }, [currentAudioIndex, audioFiles]);

  useEffect(() => {
    // Stop batch playback if manual play is started
    if (isManualPlay) {
      setIsPlaying(false);
      if (audioRef.current) {
        audioRef.current.pause();
      }
    }
  }, [isManualPlay]);

  useEffect(() => {
    // Reset batch player when switching to manual play
    if (isManualPlay && currentPlayingFile) {
      setCurrentAudioIndex(files.findIndex(f => f.id === currentPlayingFile));
    }
  }, [currentPlayingFile, isManualPlay, files]);

  const fetchAndSetAudioSource = async (fileId) => {
    try {
      setLoading(true);
      const response = await fetch(`https://gnrl-bknd-server.onrender.com/api/getAudioFile?fileId=${fileId}`, {
        method: 'GET',
        headers: {
          'Accept': 'audio/*',
        }
      });

      if (!response.ok) {
        let errorMessage;
        try {
          const errorData = await response.json();
          errorMessage = errorData.details || errorData.error || response.statusText;
        } catch {
          errorMessage = await response.text();
        }
        throw new Error(`Failed to fetch audio (${response.status}): ${errorMessage}`);
      }

      const audioBlob = await response.blob();
      if (!audioBlob.size) {
        throw new Error('Received empty audio file');
      }

      // Verify that we received an audio file
      if (!audioBlob.type.startsWith('audio/')) {
        throw new Error(`Invalid file type received: ${audioBlob.type}`);
      }

      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      const newUrl = URL.createObjectURL(audioBlob);
      setAudioUrl(newUrl);
      return newUrl;
    } catch (error) {
      console.error('Error details:', {
        message: error.message,
        fileId: fileId,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleRepeatChange = (increment) => {
    setRepeatCount(prev => Math.max(1, prev + increment));
  };

  useEffect(() => {
    if (!audioRef.current) return;

    let lastUpdateTime = 0;
    let isExplicitPause = false;

    const updateProgress = async (file, time, forceState = null) => {
      try {
        const now = Date.now();
        if (now - lastUpdateTime < 1000) {
          return;
        }
        lastUpdateTime = now;

        // Only mark as completed if we've finished all repetitions
        const completed = forceState !== null ? forceState : (currentRepetition >= repeats);

        await onUpdateProgress(file.id, {
          playTime: Math.round(time),
          completed,
          repeats: currentRepetition,
          isFromBatch: true
        });
      } catch (error) {
        console.error('Error updating progress:', error);
      }
    };

    const handleTimeUpdate = async () => {
      if (isPlaying && audioRef.current && audioFiles[currentAudioIndex]) {
        const currentFile = audioFiles[currentAudioIndex];
        const currentTime = audioRef.current.currentTime;
        const duration = audioRef.current.duration;
        
        if (duration && currentTime >= duration * 0.9) {
          await updateProgress(currentFile, currentTime, currentRepetition >= repeats);
        }
      }
    };

    const handleEnded = async () => {
      if (!audioFiles[currentAudioIndex]) return;
      
      const currentFile = audioFiles[currentAudioIndex];
      console.log(`File ${currentFile.name} ended. Current repetition: ${currentRepetition}/${repeats}`);

      try {
        // Update progress for the current repetition
        await updateProgress(currentFile, audioRef.current.duration || 30, currentRepetition >= repeats);

        if (currentRepetition < repeats) {
          // Continue with next repetition of current file
          console.log(`Starting repetition ${currentRepetition + 1} for ${currentFile.name}`);
          setCurrentRepetition(prev => prev + 1);
          
          // Wait for the gap before playing again
          await new Promise(resolve => setTimeout(resolve, audioGap * 1000));
          
          if (isPlaying && audioRef.current) {
            audioRef.current.currentTime = 0;
            await audioRef.current.play();
          }
        } else {
          // Move to next file
          console.log(`Completed all repetitions for ${currentFile.name}`);
          setCurrentRepetition(1); // Reset repetition count
          
          if (currentAudioIndex < audioFiles.length - 1) {
            await playNextAudio();
          } else {
            // End of playlist
            setIsPlaying(false);
            setCurrentAudioIndex(0);
            setCurrentRepetition(1);
            onAudioChange?.(null);
          }
        }
      } catch (error) {
        console.error('Error in handleEnded:', error);
      }
    };

    const handlePause = async () => {
      if (!audioFiles[currentAudioIndex] || !isExplicitPause) return;
      
      const currentFile = audioFiles[currentAudioIndex];
      const currentTime = audioRef.current.currentTime;
      const duration = audioRef.current.duration;
      
      await updateProgress(currentFile, currentTime, false);
      isExplicitPause = false;
    };

    audioRef.current.addEventListener('timeupdate', handleTimeUpdate);
    audioRef.current.addEventListener('ended', handleEnded);
    audioRef.current.addEventListener('pause', handlePause);
    
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', handleTimeUpdate);
        audioRef.current.removeEventListener('ended', handleEnded);
        audioRef.current.removeEventListener('pause', handlePause);
      }
    };
  }, [audioRef.current, isPlaying, currentAudioIndex, audioFiles, repeats, currentRepetition, audioGap]);

  const handlePlay = async () => {
    if (!audioFiles.length || isManualPlay) return;

    if (!isPlaying) {
      setIsPlaying(true);
      const currentFile = audioFiles[currentAudioIndex];
      onAudioChange?.(currentFile);
      
      try {
        if (!audioUrl) {
          const url = await fetchAndSetAudioSource(currentFile.id);
          if (audioRef.current && url) {
            audioRef.current.src = url;
            // Start with completed: false until we've played some audio
            await onUpdateProgress(currentFile.id, {
              playTime: 0,
              completed: false,
              repeats: 1,
              isFromBatch: true
            });
            await audioRef.current.play();
          }
        } else {
          await audioRef.current?.play();
        }
      } catch (error) {
        console.error('Error starting playback:', error);
        setIsPlaying(false);
      }
    } else {
      setIsPlaying(false);
      window.setExplicitPause?.(true);
      audioRef.current?.pause();
      onAudioChange?.(null);
    }
  };

  const playNextAudio = async () => {
    if (!isPlaying) return;
    
    try {
      // Wait for the gap before playing the next file
      await new Promise(resolve => setTimeout(resolve, audioGap * 1000));
      
      if (!isPlaying) return; // Check if still playing after gap
      
      setCurrentAudioIndex(prev => prev + 1);
      setCurrentRepetition(1); // Reset repetition count for new file
      
      const nextFile = audioFiles[currentAudioIndex + 1];
      if (nextFile) {
        onAudioChange?.(nextFile);
        const url = await fetchAndSetAudioSource(nextFile.id);
        if (audioRef.current && url && isPlaying) {
          audioRef.current.src = url;
          await onUpdateProgress(nextFile.id, {
            playTime: 0,
            completed: false,
            repeats: 1,
            isFromBatch: true
          });
          await audioRef.current.play();
        }
      }
    } catch (error) {
      console.error('Error in playNextAudio:', error);
      setIsPlaying(false);
    }
  };

  if (!audioFiles.length) {
    return null;
  }

  return (
    <div className="batch-audio-player">
      <div className="batch-controls">
        <div className="controls-left">
          <button
            onClick={handlePlay}
            disabled={loading}
            className="play-button"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <PauseCircle size={28} /> : <PlayCircle size={28} />}
          </button>
        </div>

        <div className="controls-right">
          <div className={loading ? 'loading-text' : 'status-text'}>
            {loading ? (
              <span>Loading...</span>
            ) : (
              <span>
                {currentAudioIndex + 1}/{audioFiles.length}
                {repeats > 1 && ` (${currentRepetition}/${repeats})`}
              </span>
            )}
          </div>
        </div>
      </div>

      <audio ref={audioRef} className="hidden" />
    </div>
  );
};

export default BatchAudioPlayer;
